import moment from 'moment'
import { getMinMaxKeys, minMaxParamFilters } from 'dashboard/utils'
import { resolveDateOption, dateOptionLabel } from './date-option-range'
import { dataFieldTokenPattern, dataFieldKey, formatAggregate, tokenParts } from './data-field-tokens'

// Syntax: {{filter::<key>}} or {{filter::<accessor_key>.<min|max|option>}}
//
// Tokens read the flat filters only: a filter inside an AND/OR group (filterTree)
// has no single value to print, so it renders as though unset.
//
// All resolved filter values are HTML-escaped before substitution. The
// surrounding text content is sanitized server-side at save time; this
// escape protects the *filter values themselves*, which come from live
// dashboard params and can contain user-typed text. The result is rendered
// via dangerouslySetInnerHTML, so an unescaped value would otherwise be a
// stored/reflected XSS vector. Mirrors api/app/services/charts/filter_interpolator.rb.

// Exported as a factory rather than a shared constant: the regex is global, so
// a single instance carries `lastIndex` between callers and makes matches skip.
// The text chart editor uses it to highlight tokens as they are typed.
export const filterTokenPattern = () => /\{\{\s*filter::([^}\s]+)\s*\}\}/g

const escapeHtml = (value) => {
  if (value == null || value === '') return ''
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
}

const isDateLikeString = (value) => {
  if (typeof value !== 'string') return false
  return /^\d{4}-\d{2}-\d{2}(?:T.*)?$/.test(value)
}

const formatDate = (value) => moment(value).format('M/D/YYYY')

const formatForColumn = (value, column) => {
  if (value == null || value === '') return ''
  if (column?.filter_type === 'date' || isDateLikeString(value)) {
    return formatDate(value)
  }
  return String(value)
}

const rangeDisplay = (min, max, column) => {
  const hasMin = min !== undefined && min !== null && min !== ''
  const hasMax = max !== undefined && max !== null && max !== ''
  if (hasMin && hasMax) {
    return `${formatForColumn(min, column)} to ${formatForColumn(max, column)}`
  }
  if (hasMin) return `≥ ${formatForColumn(min, column)}`
  if (hasMax) return `≤ ${formatForColumn(max, column)}`
  return ''
}

// `greater_than` columns never store their value under the accessor_key: the
// popover writes it to the same `${prefix}Min${suffix}` key a range column uses
// when the accessor is section-prefixed, and to the accessor_key itself when it
// isn't (shared/column-filters/GreaterThanFilterPopover.jsx, mirrored by
// FilterableHeader#isColumnFiltered). A bare token has to look there or it
// always resolves to nothing.
const greaterThanKey = (column) => {
  const prefix = column.section_abbreviation?.toLowerCase()
  if (!prefix || !column.accessor_key?.startsWith(prefix)) return column.accessor_key
  return getMinMaxKeys(column).minKey
}

// For date columns, when the user picks a relative option ("Previous Fiscal
// Year", "Past 30 days", etc.), the dashboard params only store the option
// code (in `${accessor_key}Option`) — the backend resolves it to actual dates
// at query time. Mirror that resolution client-side so text charts can show
// the same range live.
const datesFromOption = (column, params, municipalitySettings) => {
  if (column?.filter_type !== 'date') return null
  const option = params[`${column.accessor_key}Option`]
  if (!option) return null
  return resolveDateOption(option, municipalitySettings)
}

export const resolveFilterValue = (
  rawKey,
  params = {},
  tableColumns = [],
  municipalitySettings = null
) => {
  if (!rawKey) return ''
  const key = rawKey.trim()

  const suffixMatch = key.match(/^(.+)\.(min|max|option)$/)
  if (suffixMatch) {
    const [, baseKey, suffix] = suffixMatch
    const column = tableColumns.find((tc) => tc.accessor_key === baseKey)
    if (column) {
      if (suffix === 'option') {
        const rawOption = params[`${column.accessor_key}Option`] || ''
        return dateOptionLabel(rawOption) || rawOption
      }
      const { minKey, maxKey } = getMinMaxKeys(column)
      const direct = params[suffix === 'min' ? minKey : maxKey]
      if (direct !== undefined && direct !== null && direct !== '') {
        return formatForColumn(direct, column)
      }
      const resolved = datesFromOption(column, params, municipalitySettings)
      if (resolved) return formatForColumn(resolved[suffix], column)
    }
    return ''
  }

  const column = tableColumns.find((tc) => tc.accessor_key === key)
  if (column && minMaxParamFilters.includes(column.filter_type)) {
    const { minKey, maxKey } = getMinMaxKeys(column)
    const minVal = params[minKey]
    const maxVal = params[maxKey]
    const hasDirect =
      (minVal !== undefined && minVal !== null && minVal !== '') ||
      (maxVal !== undefined && maxVal !== null && maxVal !== '')
    if (hasDirect) return rangeDisplay(minVal, maxVal, column)
    const resolved = datesFromOption(column, params, municipalitySettings)
    if (resolved) return rangeDisplay(resolved.min, resolved.max, column)
    return ''
  }

  // Rendered as the one-sided range it is: "≥ 5000".
  if (column?.filter_type === 'greater_than') {
    return rangeDisplay(params[greaterThanKey(column)], undefined, column)
  }

  const direct = params[key]
  if (direct !== undefined && direct !== null && direct !== '') {
    if (Array.isArray(direct)) return direct.join(', ')
    if (typeof direct === 'object') return ''
    return formatForColumn(direct, column)
  }

  return ''
}

// `values` holds the aggregates the chart-data endpoint computed for this
// chart's {{Column:aggregation}} tokens. Absent, those tokens render blank —
// which is what a caller with nothing fetched yet should show.
export const interpolateFilters = (
  text,
  params = {},
  tableColumns = [],
  municipalitySettings = null,
  values = {}
) => {
  if (!text || typeof text !== 'string') return text
  if (!text.includes('{{')) return text

  // Data fields first: filter values are user-typed, and one containing
  // "{{X:sum}}" must print as typed rather than be read as a token by a later pass.
  const withDataFields = text.replace(dataFieldTokenPattern(), (...match) =>
    escapeHtml(formatAggregate((values || {})[dataFieldKey(...tokenParts(match))]))
  )

  return withDataFields.replace(filterTokenPattern(), (_match, key) =>
    escapeHtml(resolveFilterValue(key, params, tableColumns, municipalitySettings))
  )
}
